import { collection } from '@/infra/collection'
import { Member, type MemberMetadata, type MemberProps } from './Member'
import DataLoader from 'dataloader'
import { type IamApi } from '@/features/iam/api/iam-api'
import {
  type GetManyMembersRequest,
  type ListMembersRequest,
  type CreateMemberRequest,
  type MemberResource,
  type GetManyMembersByPublicIdRequest,
  type PortalResource,
  type UserSourceResource,
} from '@/features/iam/api/resources/members'
import { Id } from '@/infra/id'
import { type CursorPaged } from '@/infra/paging'
import { type CursorPagedResponse } from '@/infra/api-client'
import { type PrimeRoleFn } from '@/features/iam/roles/RoleStore'
import { type PrimeSkillFn } from '@/features/iam/skills/SkillStore'
import { type Skill } from '@/features/iam/skills/Skill'
import { type Role } from '@/features/iam/roles/Role'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type MagicLinkDestinationResource } from '@/features/iam/api/resources/magic-link'
import { User } from '@/features/iam/users/User'
import { maybeParseDate } from '@/utils/date-util'
import { isNotNil } from '@/utils/object-util'
import { mapRoleResourceToProps } from '@/features/iam/roles/role-mappers'
import { mapSkillResourceToProps } from '@/features/iam/skills/skill-mappers'
import {
  mapPermissionSetResourceToProps,
  mapPermissionToResource,
} from '@/features/iam/permissions/permission-mappers'
import { mapUserDataTypeToResource } from '@/features/iam/users/user-data-mappers'
import type { UserDataType } from '@/features/iam/users/UserData'
import type { Permissions } from '@/features/iam/permissions/Permissions'
import type { Portal } from '@/features/iam/members/portal'
import type {
  FetchMemberProps,
  PrimeMemberByPublicIdProps,
  PrimeMemberProps,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'

/**
 * Sets a member from its resource and primes the loader with it, so fetching
 * it does not hit the API.
 */
export type PrimeFullMemberFn = (resource: MemberResource) => Member

/**
 * Sets a member from its resource.
 */
export type SetMemberFn = (resource: MemberResource) => Member

export interface FetchMemberParams {
  workspaceId: Id
  userId: Id
  userPublicId?: Id
}

export interface SearchMembersQuery {
  workspaceId: Id
  searchText?: string
  /**
   * Only returns members of any of these user types.
   */
  userTypes?: UserDataType[]
  /**
   * Only returns members whose role grants this permission.
   */
  permission?: keyof Permissions
  after?: string | null
  limit?: number
}

/**
 * How a member's user signs in.
 */
export type UserSource = 'Auth0' | 'Local' | 'External'

export interface CreateMemberParams {
  workspaceId: Id
  userId?: Id
  givenName?: string
  familyName?: string
  email?: string
  phone?: string
  source?: UserSource
  roleId?: Id
  skillIds?: Id[]
  teamId?: Id
  teamRoleId?: Id
  portal?: Portal
}

export interface CreateMemberManuallyParams extends CreateMemberParams {
  email: string
}

export interface UpdateMemberParams {
  workspaceId: Id
  userPublicId: Id
  givenName?: string
  familyName?: string
  email?: string
  phone?: string
  inactive?: boolean
  maxConcurrentProviderJobs?: number
}

/**
 * Where a magic link takes its user.
 */
export type MagicLinkDestination =
  { type: 'Dashboard' } | { type: 'JobDetails'; jobId: Id }

export interface GenerateMagicLinkParams {
  workspaceId: Id
  userPublicId: Id
  destination: MagicLinkDestination
}

export interface MagicLink {
  url: string
}

export interface FetchMemberByPublicIdParams {
  workspaceId: Id
  userPublicId: Id
}

/**
 * Manages members, which are users that belong to a workspace.
 */
export class MemberStore {
  /**
   * Data loader.
   */
  private readonly _memberLoader = new DataLoader(this._fetchMany.bind(this), {
    cacheKeyFn: (params) =>
      getCompositeMemberId(params.workspace_id, params.user_id),
  })

  /**
   * Data loader using public ID.
   */
  private readonly _memberLoaderByUserPublicId = new DataLoader(
    this._fetchManyByPublicId.bind(this),
    {
      cacheKeyFn: (params) =>
        getCompositeMemberId(params.workspace_id, params.user_public_id),
    },
  )

  /**
   * Members collection.
   */
  readonly members = collection({
    // We don't use the Member `id` for anything, always workspace + user ID.
    getDataId: (data) =>
      getCompositeMemberId(data.workspace?.id, data.user?.publicId),
    getModelId: (model) =>
      getCompositeMemberId(model.workspace.id, model.user.publicId),
    create: () => new Member(),
  })

  constructor(
    private readonly _api: IamApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeRole: PrimeRoleFn,
    private readonly _primeSkill: PrimeSkillFn,
  ) {}

  /**
   * Returns a member from the collection matching the workspace and user IDs.
   */
  getMember(workspaceId: Id, userId: Id): Member | undefined {
    if (!workspaceId || !userId) {
      return undefined
    }
    return this.members.find(
      (x) => x.workspace.id === workspaceId && x.user.id === userId,
    )
  }

  /**
   * Returns a member from the collection matching the workspace and user public IDs.
   */
  getMemberByUserPublicId(
    workspaceId: Id,
    userPublicId: Id,
  ): Member | undefined {
    if (!workspaceId || !userPublicId) {
      return undefined
    }
    return this.members.find(
      (x) => x.workspace.id === workspaceId && x.user.publicId === userPublicId,
    )
  }

  /**
   * Sets and fetches the member if not been fetched before using the user public ID.
   */
  primeMemberByPublicId(props: PrimeMemberByPublicIdProps): Member {
    if (!Id.some(props.workspaceId)) {
      return Member.none
    }

    if (!Id.some(props.userPublicId)) {
      return Member.none
    }

    void this.fetchMemberByUserPublicId({
      workspaceId: props.workspaceId,
      userPublicId: props.userPublicId,
    })

    const member = this.members.set(
      this._mapMemberResourceToProps({
        workspace_id: props.workspaceId,
        user_public_id: props.userPublicId,
      }),
    )

    if (member.internalId) {
      // The member has already been hydrated from the server, so don't
      // assign any props to it
      return member
    }

    return member.set(
      this._mapMemberResourceToProps(mapPrimeMemberPropsToResource(props)),
    )
  }

  /**
   * Primes the member cache based on the specified resource.
   */
  primeFullMember(resource: MemberResource): Member {
    const member = this.members.set(this._mapMemberResourceToProps(resource))
    this._memberLoaderByUserPublicId.prime(
      {
        user_public_id: resource.user_public_id,
        workspace_id: resource.workspace_id,
      },
      member,
    )
    return member
  }

  /**
   * Sets and fetches the member if not been fetched before.
   */
  primeMember(props: PrimeMemberProps): Member {
    if (!Id.some(props.workspaceId)) {
      return Member.none
    }
    if (!Id.some(props.userId)) {
      return Member.none
    }

    void this.fetchMember({
      workspaceId: props.workspaceId,
      userId: props.userId,
      userPublicId: props.userPublicId,
    })

    const member = this.members.set(
      this._mapMemberResourceToProps({
        workspace_id: props.workspaceId,
        user_id: props.userId,
        user_public_id: props.userPublicId,
      }),
    )

    if (member.internalId) {
      // The member has already been hydrated from the server, so don't
      // assign any props to it
      return member
    }

    return member.set(
      this._mapMemberResourceToProps(mapPrimeMemberPropsToResource(props)),
    )
  }

  forWorkspace(workspaceId: Id) {
    return this.members.filter((member) => member.workspace.id === workspaceId)
  }

  /**
   * Fetch a single member from cache DataLoader or API
   */
  async fetchMember(
    params: FetchMemberParams,
    signal?: AbortSignal,
  ): Promise<Member | null> {
    const workspaceId = Id.assert(params.workspaceId)
    const userId = Id.assert(params.userId)
    const member = await (params.userPublicId
      ? this._memberLoaderByUserPublicId.load({
          workspace_id: workspaceId,
          user_public_id: params.userPublicId,
        })
      : this._memberLoader.load({
          workspace_id: workspaceId,
          user_id: userId,
        }))
    signal?.throwIfAborted()
    return member
  }

  /**
   * Fetch a single member from cache DataLoaderByUserPublicId or API
   */
  async fetchMemberByUserPublicId(
    params: FetchMemberByPublicIdParams,
    signal?: AbortSignal,
  ): Promise<Member | null> {
    const member = await this._memberLoaderByUserPublicId.load({
      workspace_id: Id.assert(params.workspaceId),
      user_public_id: Id.assert(params.userPublicId),
    })
    signal?.throwIfAborted()
    return member
  }

  /**
   * Lists members.
   */
  async listMembers(
    query: SearchMembersQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> {
    const request = mapSearchMembersQueryToRequest(query)
    const response = await this._api.members.list(
      request.workspace_id,
      request,
      signal,
    )
    return this._toMemberPage(response)
  }

  /**
   * Members whose effective permissions include `has_permission`.
   * Uses `/members/search`, which merges the platform role into the workspace role.
   */
  async listMembersByPermission(
    query: SearchMembersQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> {
    const request = mapSearchMembersQueryToRequest(query)
    const response = await this._api.members.search(
      request.workspace_id,
      request,
      signal,
    )
    return this._toMemberPage(response)
  }

  /**
   * Fetches members by workspace and user public IDs.
   */
  async fetchManyByPublicIds(
    ids: readonly FetchMemberProps[],
    signal?: AbortSignal,
  ): Promise<(Member | null)[]> {
    if (ids.some((x) => !x.workspaceId || !x.userPublicId)) {
      console.warn(new Error('No workspace or user defined'))
    }
    const members = await Promise.all(
      ids.map((id) =>
        this._memberLoaderByUserPublicId.load({
          workspace_id: id.workspaceId ?? '',
          user_public_id: id.userPublicId ?? '',
        }),
      ),
    )
    signal?.throwIfAborted()
    return members
  }

  /**
   * Adds a role to a member.
   */
  async addRole(workspaceId: Id, userPublicId: Id, roleId: Id) {
    return this._api.members
      .addRole(
        Id.assert(workspaceId),
        Id.assert(userPublicId),
        Id.assert(roleId),
      )
      .then((resource) => this.setMember(resource))
  }

  /**
   * Adds a skill to a member.
   */
  async addSkill(member: Member, skill: Skill) {
    return this._api.members
      .addSkill(
        Id.assert(member.workspace.id),
        Id.assert(member.user.publicId),
        Id.assert(skill.id),
      )
      .then((resource) => this.setMember(resource))
  }

  /**
   * Adds many skills to a member.
   */
  async addManySkillsByPublicId(
    workspace: Workspace,
    userPublicId: Id,
    skills: Skill[],
  ) {
    return this._api.members
      .addSkills(
        Id.assert(workspace.id),
        Id.assert(userPublicId),
        skills.map((skill) => Id.assert(skill.id)),
      )
      .then((resource) => this.setMember(resource))
  }

  /**
   * Removes a role from a member.
   */
  async removeRole(member: Member, role: Role) {
    return this._api.members
      .removeRole(
        Id.assert(member.workspace.id),
        Id.assert(member.user.publicId),
        Id.assert(role.id),
      )
      .then((resource) => this.setMember(resource))
  }

  /**
   * Removes a skill from a member.
   */
  async removeSkill(member: Member, skill: Skill) {
    return this._api.members
      .removeSkill(
        Id.assert(member.workspace.id),
        Id.assert(member.user.publicId),
        Id.assert(skill.id),
      )
      .then((resource) => this.setMember(resource))
  }

  /**
   * Removes many skills from a member.
   */
  async removeManySkillsByPublicId(
    workspace: Workspace,
    userPublicId: Id,
    skills: Skill[],
  ) {
    return this._api.members
      .removeSkills(
        Id.assert(workspace.id),
        Id.assert(userPublicId),
        skills.map((skill) => Id.assert(skill.id)),
      )
      .then((resource) => this.setMember(resource))
  }

  /**
   * Creates a workspace member.
   */
  createMember(params: CreateMemberParams): Promise<Member> {
    return this._api.members
      .create(mapCreateMemberParamsToRequest(params))
      .then((resource) => this.setMember(resource))
  }

  /**
   * Creates the user's member in a workspace from their profile.
   */
  createWithProfile(params: CreateMemberWithProfileParams): Promise<Member> {
    return this._api.members
      .create({
        workspace_id: Id.assert(params.workspaceId),
        user_id: Id.assert(params.userId),
        given_name: params.givenName,
        family_name: params.familyName,
        email: params.email,
        phone: params.phone,
      })
      .then((resource) => this.setMember(resource))
  }

  /**
   * Used for manually creating another member.
   */
  createMemberManually(params: CreateMemberManuallyParams): Promise<Member> {
    return this._api.members
      .createManually({
        ...mapCreateMemberParamsToRequest(params),
        email: params.email,
      })
      .then((resource) => this.setMember(resource))
  }

  /**
   * Updates a member.
   */
  async updateMember(params: UpdateMemberParams): Promise<Member> {
    return this._api.members
      .update(Id.assert(params.workspaceId), Id.assert(params.userPublicId), {
        given_name: params.givenName,
        family_name: params.familyName,
        email: params.email,
        phone: params.phone,
        inactive: params.inactive,
        max_concurrent_provider_jobs: params.maxConcurrentProviderJobs,
      })
      .then((resource) => this.setMember(resource))
  }

  /**
   * Updates a member's profile.
   */
  async updateProfile(
    member: Member,
    params: UpdateProfileParams,
  ): Promise<Member> {
    return this._api.members
      .update(Id.assert(member.workspace.id), Id.assert(member.user.publicId), {
        given_name: params.givenName,
        family_name: params.familyName,
        email: params.email,
        phone: params.phone,
        member_metadata: params.memberMetadata,
      })
      .then((resource) => this.setMember(resource))
  }

  /**
   * Updates a member's last_login date.
   */
  async updateLastLogin(member: Member) {
    return this._api.members.updateLastLogin(Id.assert(member.workspace.id))
  }

  /**
   * Updates a member's last_seen date.
   */
  async updateLastSeen(member: Member) {
    return this._api.members.updateLastSeen(Id.assert(member.workspace.id))
  }

  /**
   * Changes the avatar of the specified member.
   *
   * @param member
   * @param file
   */
  async changeAvatar(member: Member, file: File) {
    return this._api.members
      .changeAvatar(
        Id.assert(member.workspace.id),
        Id.assert(member.user.publicId),
        file,
      )
      .then((data) => (data ? this.setMember(data) : data))
  }

  async impersonate(userPublicId: Id, workspaceId: Id, callback: string) {
    return this._api.impersonation.impersonate(
      Id.assert(userPublicId),
      Id.assert(workspaceId),
      callback,
    )
  }

  /**
   * Generates a Magic Link for a given user.
   */
  async generateMagicLink(params: GenerateMagicLinkParams): Promise<MagicLink> {
    const resource = await this._api.magicLinks.generate({
      user_public_id: Id.assert(params.userPublicId),
      workspace_id: Id.assert(params.workspaceId),
      destination: mapMagicLinkDestinationToResource(params.destination),
    })
    return { url: resource.url }
  }

  /**
   * Used by the DataLoader to fetch any number of members
   */
  private async _fetchMany(ids: GetManyMembersRequest) {
    if (ids.length === 0) {
      return []
    }

    const membersToFetch = new Map<string, Member>()
    ids.forEach((params) => {
      const member = this.members.set(this._mapMemberResourceToProps(params))
      membersToFetch.set(params.workspace_id + params.user_id, member)
    })

    const members = await this._api.members.getMany(ids)
    members.forEach((item, index) => {
      if (!item) {
        // If the user is not found now, does not mean it won't be found later.
        this._memberLoader.clear(ids[index])
        const placeholder = membersToFetch.get(
          ids[index].workspace_id + ids[index].user_id,
        )
        if (placeholder) {
          this.members.remove(placeholder)
        }
      } else {
        const target = membersToFetch.get(item.workspace_id + item.user_id)
        if (target) {
          target.set(this._mapMemberResourceToProps(item))
        }
      }
    })
    return members.map(
      (r) => (r && this.getMember(r.workspace_id, r.user_id)) ?? null,
    )
  }

  private async _fetchManyByPublicId(ids: GetManyMembersByPublicIdRequest) {
    if (ids.length === 0) {
      return []
    }

    const membersToFetch = new Map<string, Member>()
    ids.forEach((params) => {
      const member = this.members.set(this._mapMemberResourceToProps(params))
      membersToFetch.set(params.workspace_id + params.user_public_id, member)
    })

    const members = await this._api.members.getManyByPublicId(ids)
    members.forEach((item, index) => {
      if (!item) {
        // If the user is not found now, does not mean it won't be found later.
        this._memberLoaderByUserPublicId.clear(ids[index])
        const placeholder = membersToFetch.get(
          ids[index].workspace_id + ids[index].user_public_id,
        )
        if (placeholder) {
          this.members.remove(placeholder)
        }
      } else {
        const target = membersToFetch.get(
          item.workspace_id + item.user_public_id,
        )
        if (target) {
          target.set(this._mapMemberResourceToProps(item))
        }
      }
    })
    return members.map(
      (r) =>
        (r && this.getMemberByUserPublicId(r.workspace_id, r.user_public_id)) ??
        null,
    )
  }

  /**
   * Sets the member from its resource.
   */
  setMember(resource: MemberResource): Member {
    return this.members.set(this._mapMemberResourceToProps(resource))
  }

  private _toMemberPage(
    response: CursorPagedResponse<MemberResource>,
  ): CursorPaged<Member> {
    return {
      data: this.members.set(response.data.map(this._mapMemberResourceToProps)),
      next: response.cursor,
      total: response.total,
    }
  }

  private readonly _mapMemberResourceToProps = (
    resource: Partial<MemberResource>,
  ): Partial<MemberProps> => {
    let user: User | undefined
    if (resource.user_public_id || resource.user_id) {
      user = this._primeUser({
        id: resource.user_id,
        email: resource.email,
        publicId: resource.user_public_id,
        givenName: resource.given_name,
        familyName: resource.family_name,
        picture: resource.picture,
      })

      if (user === User.none) {
        user = undefined
      }
    }

    return {
      user,
      internalId: resource.id,
      givenName: resource.given_name,
      familyName: resource.family_name,
      displayName: resource.display_name,
      email: resource.email,
      picture: resource.picture,
      phone: resource.phone,
      inactive: resource.inactive,
      memberMetadata: resource.member_metadata,
      lastLogin: maybeParseDate(resource.last_login),
      lastSeen: maybeParseDate(resource.last_seen),
      maxConcurrentProviderJobs: resource.max_concurrent_provider_jobs,
      approvedTermsVersion: resource.approved_terms_version,
      referrer: resource.referrer,
      permissions:
        resource.computed_permissions &&
        mapPermissionSetResourceToProps(resource.computed_permissions),
      roles: resource.roles
        ? resource.roles
            .map((r) =>
              r.role ? this._primeRole(mapRoleResourceToProps(r.role)) : null,
            )
            .filter(isNotNil)
        : undefined,
      skills: resource.skills
        ? resource.skills
            .map((s) =>
              s.skill
                ? this._primeSkill(mapSkillResourceToProps(s.skill))
                : null,
            )
            .filter(isNotNil)
        : undefined,
      dateCreated: maybeParseDate(resource.date_created),
      dateModified: maybeParseDate(resource.date_modified),
      workspace: resource.workspace_id
        ? this._primeWorkspace({ id: resource.workspace_id })
        : undefined,
    }
  }
}

function mapSearchMembersQueryToRequest(
  query: SearchMembersQuery,
): ListMembersRequest {
  return {
    workspace_id: Id.assert(query.workspaceId),
    search_text: query.searchText,
    user_types: query.userTypes?.map(mapUserDataTypeToResource),
    has_permission:
      query.permission && mapPermissionToResource(query.permission),
    after: query.after,
    limit: query.limit,
  }
}

function mapCreateMemberParamsToRequest(
  params: CreateMemberParams,
): CreateMemberRequest {
  return {
    workspace_id: Id.assert(params.workspaceId),
    user_id: params.userId,
    given_name: params.givenName,
    family_name: params.familyName,
    email: params.email,
    phone: params.phone,
    source: params.source && mapUserSourceToResource(params.source),
    role_id: params.roleId,
    skill_ids: params.skillIds?.map(Id.assert),
    team_id: params.teamId,
    team_role_id: params.teamRoleId,
    portal: params.portal && mapPortalToResource(params.portal),
  }
}

function mapUserSourceToResource(source: UserSource): UserSourceResource {
  switch (source) {
    case 'Auth0':
      return 'AUTH0'
    case 'Local':
      return 'LOCAL'
    case 'External':
      return 'EXTERNAL'
  }
}

function mapPortalToResource(portal: Portal): PortalResource {
  switch (portal) {
    case 'CustomerPortal':
      return 'CustomerPortal'
    case 'WorkPortal':
      return 'Portal'
    case 'Txf':
      return 'TXF'
  }
}

function mapMagicLinkDestinationToResource(
  destination: MagicLinkDestination,
): MagicLinkDestinationResource {
  switch (destination.type) {
    case 'Dashboard':
      return { type: 'DASHBOARD' }
    case 'JobDetails':
      return { type: 'JOB_DETAILS', job_id: Id.assert(destination.jobId) }
  }
}

function getCompositeMemberId(workspaceId?: Id, userId?: Id) {
  return workspaceId && userId ? `${workspaceId}/${userId}` : undefined
}

function mapPrimeMemberPropsToResource(
  props: PrimeMemberProps | PrimeMemberByPublicIdProps,
): Partial<MemberResource> {
  return {
    workspace_id: props.workspaceId,
    user_id: props.userId,
    user_public_id: props.userPublicId,
    given_name: props.givenName,
    family_name: props.familyName,
    display_name: props.displayName,
    email: props.email,
    picture: props.picture,
  }
}

/**
 * Params for updating a member's profile.
 */
export interface UpdateProfileParams {
  givenName?: string
  familyName?: string
  email?: string
  phone?: string
  memberMetadata?: MemberMetadata
}

/**
 * Params for creating the user's member from their profile.
 */
export interface CreateMemberWithProfileParams {
  workspaceId: Id
  userId: Id
  givenName: string
  familyName: string
  email: string
  phone: string
}
